import { useMemo } from "react";
import { useDailySalesQuery } from "../../query/adminDailySalesQuery";
import { formatMoney } from "../../utils/formatMoney";
import {
  aggregatePeriodDailySales,
  getPrimaryGrossAmount,
  isSalesToday,
} from "../../utils/dailySalesUtils";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { StatBox } from "../../components/StatBox";
import { useTranslation } from "react-i18next";
import { Row } from "../../components/Row";
import { spacing } from "@styling/sizing";

interface SalesDashboardValueBoxesProps {
  resellerId?: string;
  from: string;
  to: string;
  periodLabel: string;
  date: string;
  selectedDateLabel: string;
}

export const SalesDashboardValueBoxes = ({
  resellerId,
  from,
  to,
  periodLabel,
  date,
  selectedDateLabel,
}: SalesDashboardValueBoxesProps) => {
  const { t } = useTranslation();
  const { data: periodStats } = useDailySalesQuery(from, to, resellerId);
  const { data: dayStatsList } = useDailySalesQuery(date, date, resellerId);
  const isToday = isSalesToday(date);

  const periodTotal = useMemo(
    () => aggregatePeriodDailySales(periodStats ?? []),
    [periodStats]
  );

  const dayStats = dayStatsList?.[0];
  const sekGross = dayStats
    ? getPrimaryGrossAmount(dayStats.grossAmountByCurrency)
    : null;

  return (
    <Row $gap={spacing.x2}>
      <StatBox
        label={t("adminDailySales.periodTotalSales", { period: periodLabel })}
        value={
          periodTotal.grossAmount ? formatMoney(periodTotal.grossAmount) : "-"
        }
      />
      <StatBox
        label={
          isToday
            ? t("adminDailySales.receiptsToday")
            : t("adminDailySales.receiptsOnDate", { date: selectedDateLabel })
        }
        value={dayStats?.receiptCount ?? "-"}
      />
      <StatBox
        label={
          isToday
            ? t("adminDailySales.grossSalesToday", { currency: Currency.SEK })
            : t("adminDailySales.grossSalesOnDate", {
                currency: Currency.SEK,
                date: selectedDateLabel,
              })
        }
        value={sekGross ? formatMoney(sekGross) : "-"}
      />
    </Row>
  );
};
